import * as L from 'leaflet';
import * as esri from 'esri-leaflet';
import * as turf from '@turf/turf';
import * as _ from 'lodash';
import {
  generateTable,
  getEarthquakeMarker,
  getLegendItems,
  getStationMarker,
  getTargetMarker,
  getTrianglePolygon,
  loadEarthquakeData,
  toggleSaveUpload,
  toggleSidebar
} from './map-helpers';
import { getRandPointNearCenter, getSDistFromTime, getTravelTime, searchBounds } from './seismology-helpers';
import { haversineDistance } from './helpers';
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch/lib';
import { ModalService } from './modals';
import { saveConfiguration } from './advanced-helpers';
const pbbData = require('../data/pbb.json');

const GLOBAL_DELAY_TIME = 2;
const BACKGROUND_MIN_MAG = 6;
const GLOBAL_MAX_DIST = 500;

export class MapComponent {
  constructor() {
    this.modalService = new ModalService();

    this.earthquakeMarker = null;
    this.stationMarkers = [];
    this.targetMarkers = [];
    this.activeStationMarkers = [];
    this.advanceMode = false;

    this.map = L.map('map');
    this.baseLayer = null;
    this.earthquakesLayer = null;
    this.layerLabels = null;
    this.legend = null;
    this.circles = [];
    this.areas = [];
    this.file = null;
    this.fileContent = null;
    this.baseMapSelection = null;
    this.normalModeButton = null;

    this.calc = _.debounce(this.calcAction, 500);
    this.onTargetButtonClick = _.debounce(this.onTargetButtonClickAction, 500);
    this.onStationButtonClick = _.debounce(this.onStationButtonClickAction, 500);
    this.onEarthquakeButtonClick = _.debounce(this.onEarthquakeButtonClickAction, 500);
    this.onResetMapButtonClick = _.debounce(this.onResetMapButtonClickAction, 500);

    this.initModeModifiers();
    this.initMap();
    this.initModeSelector();
    this.initAdvancedListeners();
  }

  initMap() {
    document.getElementById(
      'earthquake-intensity'
    ).innerText = `Historical Earthquake > ${BACKGROUND_MIN_MAG.toString()}`;

    this.map.on('load', this.onMapLoad.bind(this));
    window.myMap = this.map;

    L.control.scale().addTo(this.map);

    this.initBaseMap();

    this.map.on('moveend', this.plotEarthquakes.bind(this));
    this.map.setView([0, 0], 3);

    this.initSearch();
  }

  onMapLoad() {
    this.plotEarthquakes();
    L.geoJSON(pbbData, { color: 'grey' }).addTo(this.map);
  }

  initModeModifiers() {
    toggleSidebar(this.advanceMode);
    toggleSaveUpload(this.advanceMode);
  }

  initAdvancedListeners() {
    document.getElementById('saveFileButton').addEventListener('click', this.onSaveFile.bind(this));
    document.getElementById('useFileButton').addEventListener('click', this.onUseConfiguration.bind(this));
    document.getElementById('uploadFile').addEventListener('change', this.onUploadFile.bind(this));
  }

  onSaveFile() {
    const saveObject = {
      targetMakers: this.targetMarkers.map((marker) =>
        Object.assign(marker.getLatLng(), { name: marker.placeName, isActive: marker.isActive })
      ),
      stationMarkers: this.stationMarkers.map((marker) =>
        Object.assign(marker.getLatLng(), { name: marker.stationName, isActive: marker.isActive })
      ),
      earthquakeMarker: this.earthquakeMarker ? this.earthquakeMarker.getLatLng() : null,
      mapCenter: this.map.getCenter(),
      mapZoom: this.map.getZoom(),
      mapLayer: this.baseMapSelection
    };

    saveConfiguration(saveObject);
  }

  onUseConfiguration() {
    try {
      this.rebuildMap(JSON.parse(this.fileContent));
    } catch (e) {
      console.error(e);
    }
  }

  onUploadFile(e) {
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    this.file = file;

    const reader = new FileReader();
    reader.onload = (e) => {
      this.fileContent = e.target.result;
    };
    reader.readAsText(file);
  }

  initModeSelector() {
    this.normalModeButton = document.getElementById('set-mode-normal');
    this.advancedModeButton = document.getElementById('set-mode-advanced');

    (this.advanceMode ? this.normalModeButton : this.advancedModeButton).classList.add('button--unselected');

    this.normalModeButton.addEventListener('click', this.onModeChange.bind(this));
    this.advancedModeButton.addEventListener('click', this.onModeChange.bind(this));
  }

  onModeChange(e) {
    this.normalModeButton.classList.remove('button--unselected');
    this.advancedModeButton.classList.remove('button--unselected');
    if (e.target.id === 'set-mode-normal') {
      this.advancedModeButton.classList.add('button--unselected');
      this.advanceMode = false;
    } else {
      this.normalModeButton.classList.add('button--unselected');
      this.advanceMode = true;
      this.modalService.showAdvancedModeModal();
    }

    this.initModeModifiers();
    this.calc();
  }

  rebuildMap(savedConfiguration) {
    this.clearMap();

    document.getElementById('import-summary').innerText = `Loaded: ${
      savedConfiguration.earthquakeMarker ? 'Epicenter' : 'No Epicenter'
    }, ${savedConfiguration.targetMakers.length} Target Sites, ${savedConfiguration.stationMarkers.length} Stations`;

    if (savedConfiguration.mapCenter) {
      this.map.setView(
        [savedConfiguration.mapCenter.lat, savedConfiguration.mapCenter.lng],
        savedConfiguration.mapZoom ? savedConfiguration.mapZoom : 7
      );
    }

    if (savedConfiguration.mapLayer) {
      this.setBasemap(savedConfiguration.mapLayer);
      document.querySelectorAll('#basemaps option').forEach((option) => {
        if (option.getAttribute('value') === savedConfiguration.mapLayer) {
          option.setAttribute('selected', 'true');
        } else {
          option.removeAttribute('selected');
        }
      });
    }

    if (savedConfiguration.targetMakers) {
      this.addLegend();
      savedConfiguration.targetMakers.forEach((marker) => {
        this.addTargetMarker([marker.lat, marker.lng], marker.name);
      });
    }

    if (savedConfiguration.stationMarkers) {
      this.addLegend();
      savedConfiguration.stationMarkers.forEach((marker) => {
        this.addStationMarker({ lat: marker.lat, lng: marker.lng }, marker.name);
      });
    }

    if (savedConfiguration.earthquakeMarker) {
      this.addLegend();
      this.addEarthquakeMarker([savedConfiguration.earthquakeMarker.lat, savedConfiguration.earthquakeMarker.lng]);
      this.calc();
    }
  }

  clearMap() {
    document.getElementById('import-summary').innerText = '';
    document.getElementById('sidebar-content').innerHTML = '';

    [
      ...this.stationMarkers,
      ...this.targetMarkers,
      this.earthquakeMarker,
      ...this.circles.map((circle) => circle.textMarker),
      ...this.circles,
      ...this.areas
    ].forEach((marker) => {
      if (marker) {
        this.map.removeLayer(marker);
      }
    });

    this.stationMarkers = [];
    this.targetMarkers = [];
    this.circles = [];
    this.areas = [];
    this.earthquakeMarker = null;
  }

  initBaseMap() {
    this.baseLayer = esri.basemapLayer('DarkGray').addTo(this.map);

    document.getElementById('basemaps').addEventListener('change', (e) => {
      const basemap = e.target.value;
      this.setBasemap(basemap);
    });

    this.setBasemap('DarkGray');
  }

  initSearch() {
    const search = new GeoSearchControl({
      showMarker: false,
      provider: new OpenStreetMapProvider(),
      style: 'bar',
      autoClose: true
    });
    this.map.addControl(search);

    this.map.on('geosearch/showlocation', this.onLocationChange.bind(this));
  }

  onLocationChange() {
    this.addLegend();
    this.modalService.showIntroModal();
  }

  setBasemap(basemap) {
    this.baseMapSelection = basemap;

    if (this.baseLayer) {
      this.map.removeLayer(this.baseLayer);
    }

    this.baseLayer = esri.basemapLayer(basemap);

    this.map.addLayer(this.baseLayer);

    if (this.layerLabels) {
      this.map.removeLayer(this.layerLabels);
    }

    if (
      basemap === 'ShadedRelief' ||
      basemap === 'Oceans' ||
      basemap === 'Gray' ||
      basemap === 'DarkGray' ||
      basemap === 'Terrain'
    ) {
      this.layerLabels = esri.basemapLayer(basemap + 'Labels');
      this.map.addLayer(this.layerLabels);
    } else if (basemap.includes('Imagery')) {
      this.layerLabels = esri.basemapLayer('ImageryLabels');
      this.map.addLayer(this.layerLabels);
    }
  }

  plotEarthquakes() {
    loadEarthquakeData(this.map, BACKGROUND_MIN_MAG).then((out) => {
      if (this.earthquakesLayer) {
        this.earthquakesLayer.remove();
      }
      this.earthquakesLayer = L.geoJSON(out, {
        pointToLayer: function (feature, latlng) {
          return L.circleMarker(latlng, {
            radius: 8,
            fillColor: '#0058ff',
            color: '#FFF',
            weight: 1,
            opacity: 1,
            fillOpacity: 0.8
          });
        }
      }).addTo(this.map);
    });
  }

  addLegend() {
    if (this.legend) {
      return;
    }

    this.legend = L.control({ position: 'topright' });
    this.legend.onAdd = () => {
      const div = document.createElement('DIV');
      div.classList.add('legendDiv');
      div.innerHTML = `${getLegendItems().join('')}`;

      return div;
    };

    this.legend.addTo(this.map);
    this.initLegendButtonsListeners();
  }

  initLegendButtonsListeners() {
    document.getElementById('addTargetButton').addEventListener('click', this.onTargetButtonClick.bind(this));
    document.getElementById('addStationButton').addEventListener('click', this.onStationButtonClick.bind(this));
    document.getElementById('addEarthquakeButton').addEventListener('click', this.onEarthquakeButtonClick.bind(this));
    document.getElementById('resetMapButton').addEventListener('click', this.onResetMapButtonClick.bind(this));
  }

  onTargetButtonClickAction() {
    this.addTarget();
    this.calc();
  }

  onStationButtonClickAction() {
    if (!this.stationMarkers.length) {
      this.modalService.showStationsModal();
    }
    this.addStationMarker(getRandPointNearCenter(this.map));
    this.calc();
  }

  onEarthquakeButtonClickAction() {
    const hasTargetSites = !!this.targetMarkers.length;
    this.modalService.showEarthquakeModal(hasTargetSites);
    this.addEarthquakeMarker();
    this.calc();
  }

  onResetMapButtonClickAction() {
    this.clearMap();
    document.getElementById('addEarthquakeButton').style.display = 'inline-block';
    if (!this.advanceMode) {
      toggleSidebar(false);
    }
  }

  addEarthquakeMarker(location) {
    this.earthquakeMarker = L.marker(location ? location : getRandPointNearCenter(this.map), {
      draggable: 'true',
      icon: getEarthquakeMarker()
    })
      .addTo(this.map)
      .bindTooltip('Earthquake');
    this.earthquakeMarker.on('dragend', this.calc.bind(this));
    document.getElementById('addEarthquakeButton').style.display = 'none';
  }

  addTargetMarker(loc, targetName) {
    const targetMarker = L.marker(loc, { draggable: 'true', icon: getTargetMarker() })
      .addTo(this.map)
      .bindTooltip(targetName);
    targetMarker.placeName = targetName;
    targetMarker.isActive = true;
    targetMarker.on('dragend', this.calc.bind(this));
    targetMarker.on('contextmenu', this.onToggleMarker.bind(this));

    this.targetMarkers.push(targetMarker);
  }

  addTarget() {
    const targetName = prompt('Target Name', '');
    if (targetName != null) {
      this.addTargetMarker(getRandPointNearCenter(this.map), targetName);
    }
  }

  onToggleMarker(e) {
    const marker = e.target;
    marker.setIcon(marker.placeName ? getTargetMarker(marker.isActive) : getStationMarker(marker.isActive));
    marker.isActive = !marker.isActive;
    this.calc();
  }

  addStationMarker(loc, name) {
    const stationName = name ? name : `Station ${this.stationMarkers.length + 1}`;
    const stationMarker = L.marker(loc, { draggable: 'true', icon: getStationMarker(false) })
      .addTo(this.map)
      .bindTooltip(stationName);
    stationMarker.stationName = stationName;
    stationMarker.isActive = true;
    stationMarker.on('dragend', this.calc.bind(this));
    stationMarker.on('contextmenu', this.onToggleMarker.bind(this));

    this.stationMarkers.push(stationMarker);
  }

  addCircleAtSec(earthquakeMarker, sec, secText, color, opacity = 1) {
    const radius = getSDistFromTime(sec);
    const circle = L.circle(earthquakeMarker.getLatLng(), {
      color: color,
      opacity: opacity,
      fillColor: null,
      fillOpacity: 0.0,
      radius: radius * 1000
    }).addTo(this.map);

    const myIcon = L.divIcon({ className: 'cnt text-' + color, html: secText });
    const loc = earthquakeMarker.getLatLng();
    const dlat = radius / 104;
    circle.textMarker = L.marker([loc.lat + dlat, loc.lng], { icon: myIcon }).addTo(this.map);
    this.circles.push(circle);
  }

  plotUncertainty(eventLatLon, nearestStationLocDist, nearestStationLoc, unsorted_pTimes) {
    let intersection = null;

    this.activeStationMarkers.forEach((stationMarker) => {
      const stationLatLon = stationMarker.getLatLng();
      const dist = haversineDistance([eventLatLon.lng, eventLatLon.lat], [stationLatLon.lng, stationLatLon.lat]);
      if (dist < 500000) {
        // 500 km
        const geoJsonPolygon = getTrianglePolygon(
          [stationLatLon.lng, stationLatLon.lat],
          [eventLatLon.lng, eventLatLon.lat]
        );
        intersection = intersection === null ? geoJsonPolygon : turf.intersect(intersection, geoJsonPolygon);
      }
    });

    if (intersection !== null) {
      const intersectionPolygon = L.geoJSON(intersection).addTo(this.map);
      this.areas.push(intersectionPolygon);
    }

    let minDist = getSDistFromTime(getTravelTime(nearestStationLocDist / 1000).s - 1);
    if (!minDist) {
      minDist = 0;
    }
    const maxDist = getSDistFromTime(getTravelTime(nearestStationLocDist / 1000).s + 1);

    const minCircleGeoJson = turf.circle(nearestStationLoc, minDist, { steps: 3600 });
    const triangleGeoJson = getTrianglePolygon(nearestStationLoc, [eventLatLon.lng, eventLatLon.lat]);
    const differenceGeoJson = turf.difference(triangleGeoJson, minCircleGeoJson);
    const maxCircleGeoJson = turf.circle(nearestStationLoc, maxDist, { steps: 3600 });
    const areaGeoJson = turf.intersect(differenceGeoJson, maxCircleGeoJson);
    this.areas.push(L.geoJSON(areaGeoJson, { color: 'purple' }).addTo(this.map));

    // uncertainty of 4p system
    // if (unsorted_pTimes.length > 1) {
    //   const minPTime = Math.min(...unsorted_pTimes);
    //
    //   for (let i = 0; i < unsorted_pTimes.length; i++) {
    //     unsorted_pTimes[i] = unsorted_pTimes[i] - minPTime;
    //   }
    //
    //   const result = searchBounds(
    //     eventLatLon.lng,
    //     eventLatLon.lat,
    //     0.5,
    //     1.0 / 111.111,
    //     unsorted_pTimes,
    //     this.activeStationMarkers
    //   );
    //   const points = [];
    //   result.forEach((loc) => {
    //     points.push(turf.point(loc));
    //   });
    //   const hull = turf.convex(turf.featureCollection(points));
    //   this.areas.push(L.geoJSON(hull, { color: 'black' }).addTo(this.map));
    // }
  }

  calcAction() {
    if (this.earthquakeMarker === undefined || this.earthquakeMarker === null) {
      return;
    }

    this.activeStationMarkers = this.stationMarkers.filter((marker) => marker.isActive);

    if (this.activeStationMarkers.length < 1) {
      return;
    }

    this.circles.forEach((circle) => {
      circle.remove();
      circle.textMarker.remove();
    });

    this.areas.forEach((triangle) => {
      triangle.remove();
    });

    const eventLatLon = this.earthquakeMarker.getLatLng();
    const times = [];
    const unsorted_pTimes = [];
    let nearestStationLoc = [0, 0];
    let nearestStationLocDist = 1000000000000;

    this.activeStationMarkers.forEach((stationMarker) => {
      const stationLatLon = stationMarker.getLatLng();

      const dist = haversineDistance([eventLatLon.lng, eventLatLon.lat], [stationLatLon.lng, stationLatLon.lat]);
      times.push(getTravelTime(dist / 1000));
      unsorted_pTimes.push(getTravelTime(dist / 1000).p);
      if (dist < nearestStationLocDist) {
        nearestStationLoc = [stationLatLon.lng, stationLatLon.lat];
        nearestStationLocDist = dist;
      }
    });

    times.sort(function (a, b) {
      return a.p - b.p;
    });

    const cols = [['', 'SeismicAI system']];

    const alertpsTime = times[0].s + GLOBAL_DELAY_TIME;
    const alert2pTime = times.length > 1 ? times[1].p + GLOBAL_DELAY_TIME : 0;
    const alert4pTime = times.length > 3 ? times[3].p + GLOBAL_DELAY_TIME : 'A minimum of 4 stations is required';
    const minAlertTime = alert2pTime ? Math.min(alert2pTime, alertpsTime) : alertpsTime;

    cols.push(['Seconds to issue alert <i class="tooltip__anchor" id="time-to-alert-tooltip">?</i>', minAlertTime]);

    this.addCircleAtSec(this.earthquakeMarker, minAlertTime, '0s', 'red');
    this.addCircleAtSec(this.earthquakeMarker, minAlertTime + 10, '10s', 'orange');
    this.addCircleAtSec(this.earthquakeMarker, minAlertTime + 20, '20s', 'yellow');
    this.addCircleAtSec(this.earthquakeMarker, minAlertTime + 30, '30s', 'green');

    this.targetMarkers.forEach((targetMarker) => {
      if (!targetMarker.isActive) {
        return;
      }

      const targetLatLon = targetMarker.getLatLng();
      const dist = haversineDistance([eventLatLon.lng, eventLatLon.lat], [targetLatLon.lng, targetLatLon.lat]);
      const t = getTravelTime(dist / 1000).s;
      const alertTime2p = alert2pTime ? Math.max(0, t - alert2pTime) : '';
      const alertTimeps = Math.max(0, t - alertpsTime);
      const alertTime4p = times.length > 3 ? Math.max(0, t - alert4pTime) : '';

      cols.push([targetMarker.placeName, Math.max(alertTime2p, alertTimeps)]);
    });

    // make the table

    generateTable(cols);
    toggleSidebar(true);

    if (this.advanceMode) {
      this.plotUncertainty(eventLatLon, nearestStationLocDist, nearestStationLoc, unsorted_pTimes);
    }
  }
}
